// Scale and window maths: SVG size, panel frame, domains, scales, ticks, nearest sample, path strings.
import type { Frame, OracleStep, SeriesRow, View } from "./types";

export type Point = [number, number];

/** The drawing size from the element's client box, with the fallbacks chart.js used. */
export function svgSize(clientWidth: number, clientHeight: number): { W: number; H: number } {
  return { W: Math.max(320, Math.round(clientWidth || 1200)), H: Math.max(240, Math.round(clientHeight || 420)) };
}

/** Paddings and the two panels (main above, gap panel below) for a W by H drawing. */
export function frameFor(W: number, H: number): Frame {
  const padL = 6, padR = Math.min(96, Math.max(84, W * 0.08)), padT = 30, padB = 26;
  const gapH = Math.round((H - padT - padB) * 0.26), split = 22;
  const mainTop = padT, mainBot = H - padB - gapH - split, gapTop = mainBot + split, gapBot = H - padB;
  return { W, H, padL, padR, padT, padB, gapH, split, mainTop, mainBot, gapTop, gapBot };
}

/** [t0, t1]: first row to the latest of the last row, 20 minutes past the last recorded sample, and one hour. */
export function timeDomain(rows: SeriesRow[], rec: SeriesRow[]): { t0: number; t1: number } | null {
  const first = rows[0], last = rows.at(-1);
  if (!first || !last) return null;
  const t0 = first.t;
  const lastRec = rec.at(-1)?.t ?? t0;
  return { t0, t1: Math.max(last.t, lastRec + 20 * 60000, t0 + 3600000) };
}

/** Main panel domain: pool and oracle values, padded by 18 percent of the span (at least 0.4 percent of the level). */
export function priceDomain(rows: SeriesRow[]): { lo: number; hi: number } {
  const vals: number[] = [];
  for (const r of rows) {
    if (r.pool != null) vals.push(r.pool);
    if (r.oracle != null) vals.push(r.oracle);
  }
  let lo = Math.min(...vals), hi = Math.max(...vals);
  const span = Math.max(hi - lo, (hi || 1) * 0.004);
  lo -= span * 0.18;
  hi += span * 0.18;
  return { lo, hi };
}

/** Gap panel domain: the basis values and zero, padded by 15 percent of the span (at least 0.5 points). */
export function basisDomain(rows: SeriesRow[]): { blo: number; bhi: number } {
  const bs: number[] = [];
  for (const r of rows) if (r.basis != null) bs.push(r.basis);
  let blo = Math.min(0, ...bs), bhi = Math.max(0, ...bs);
  const bspan = Math.max(bhi - blo, 0.5);
  blo -= bspan * 0.15;
  bhi += bspan * 0.15;
  return { blo, bhi };
}

/** Frame, domains and scales for the rows that have a pool or an oracle value. Null when there are no rows. */
export function buildView(rows: SeriesRow[], rec: SeriesRow[], W: number, H: number, steps: OracleStep[], sym: string): View | null {
  const dom = timeDomain(rows, rec);
  if (!dom) return null;
  const frame = frameFor(W, H);
  const { t0, t1 } = dom;
  const { lo, hi } = priceDomain(rows);
  const { blo, bhi } = basisDomain(rows);
  const x = (t: number) => frame.padL + ((t - t0) / (t1 - t0)) * (W - frame.padL - frame.padR);
  const y = (v: number) => frame.mainTop + (1 - (v - lo) / (hi - lo)) * (frame.mainBot - frame.mainTop);
  const yg = (v: number) => frame.gapTop + (1 - (v - blo) / (bhi - blo)) * (frame.gapBot - frame.gapTop);
  return { ...frame, sym, rows, steps, t0, t1, lo, hi, blo, bhi, x, y, yg };
}

/** Inverse of the x scale: the time under an SVG x coordinate. */
export function timeAt(view: Pick<View, "t0" | "t1" | "padL" | "padR" | "W">, px: number): number {
  return view.t0 + ((px - view.padL) / (view.W - view.padL - view.padR)) * (view.t1 - view.t0);
}

/** About n round tick values between lo and hi (steps of 1, 2, 2.5, 5, 10 times a power of ten). */
/** Pixels a day label needs on the time axis, text plus gap, at the 13 px label size. */
export const DAY_LABEL_PX = { long: 82, short: 58 } as const;
const DAY_STEPS = [1, 2, 3, 4, 7, 14, 28];

/**
 * Which 00:00 UTC ticks get a label for `dayPx` pixels per day: "Mon 21 Sep" on every day when it fits, else
 * "21 Sep" on every `every`-th day (by UTC day number, so the labelled days do not move as the window slides).
 */
export function dayLabelPlan(dayPx: number): { every: number; short: boolean } {
  if (dayPx >= DAY_LABEL_PX.long) return { every: 1, short: false };
  const every = DAY_STEPS.find((n) => n * dayPx >= DAY_LABEL_PX.short) ?? DAY_STEPS[DAY_STEPS.length - 1] ?? 28;
  return { every, short: true };
}

export function niceTicks(lo: number, hi: number, n: number): number[] {
  const span = hi - lo, raw = span / n, mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => span / s <= n) ?? mag * 10;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(+v.toFixed(10));
  return out;
}

/** The row whose time is closest to t (binary search over rows sorted by t). */
export function nearest<T extends { t: number }>(rows: T[], t: number): T | undefined {
  let lo = 0, hi = rows.length - 1;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    const row = rows[m];
    if (row && row.t < t) lo = m + 1;
    else hi = m;
  }
  const prev = rows[lo - 1], cur = rows[lo];
  if (lo > 0 && prev && cur && Math.abs(prev.t - t) < Math.abs(cur.t - t)) return prev;
  return cur;
}

/** SVG path data through the points; a null lifts the pen. */
export function pathOf(pts: (Point | null)[]): string {
  let d = "", pen = false;
  for (const p of pts) {
    if (p == null) { pen = false; continue; }
    d += (pen ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1);
    pen = true;
  }
  return d;
}
